import { useState } from "react";
import { Modal, Table, Form, Button } from "react-bootstrap";
import { useQuery } from "react-query";
import ReactPaginate from "react-paginate";
import currency from "currency.js";
import { toast } from "react-toastify";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import queryString from "query-string";

import useDebounce, { useBackendUrl } from "../../../utils/hooks";
import {
  formatDate,
  formatForQty,
  paginationOptions,
} from "../../../utils/helpers";

export default function DamageBatchPicker({
  show,
  onHide,
  onAdd,
  existingBarcodes = [],
}) {
  const backendUrl = useBackendUrl();

  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 20,
    q: "",
    model: "StockBatches",
    getItemFrom: "StockBatches",
    withQuantity: true,
    withExpiry: true,
  });

  // Keyed by Bar_Code, holds the full row + chosen quantity. Storing the
  // whole row (not just the barcode) means selections survive pagination,
  // since the source row won't still be in `data.items` after paging away.
  const [selected, setSelected] = useState({});
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const debouncedQueryParams = useDebounce(queryParams, 500);

  const getBatchItems = async debouncedQueryParams => {
    let response = await fetch(
      `${backendUrl}/api/items?${queryString.stringify(debouncedQueryParams)}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    return data;
  };

  const { data = { items: [], count: 0 }, isFetching } = useQuery(
    ["DAMAGE_BATCH_PICKER", debouncedQueryParams],
    () => getBatchItems(debouncedQueryParams),
    {
      enabled: show,
      keepPreviousData: true,
      onError: () => toast.error("Unable to load batches, try again"),
    }
  );

  const isAlreadyAdded = barCode => existingBarcodes.includes(barCode);

  const toggleChecked = batch => {
    if (isAlreadyAdded(batch.Bar_Code)) return;

    setSelected(prev => {
      const updated = { ...prev };
      if (updated[batch.Bar_Code]) {
        delete updated[batch.Bar_Code];
      } else {
        updated[batch.Bar_Code] = { ...batch, quantity: "" };
      }
      return updated;
    });
  };

  const handleQuantityChange = (batch, value) => {
    setSelected(prev => ({
      ...prev,
      [batch.Bar_Code]: { ...batch, ...prev[batch.Bar_Code], quantity: value },
    }));
  };

  const selectedList = Object.values(selected);

  const handleDone = () => {
    if (selectedList.length === 0) {
      onHide();
      return;
    }

    const missingQty = selectedList.filter(el => Number(el.quantity || 0) <= 0);

    if (missingQty.length > 0) {
      toast.error("Enter a quantity for every selected batch");
      return;
    }

    const invalidQty = selectedList.filter(
      el => Number(el.quantity) > Number(el.Quantity)
    );

    if (invalidQty.length > 0) {
      toast.error(
        `Qty for ${invalidQty[0].ItemName} cannot exceed available quantity (${
          invalidQty[0].Quantity
        })`
      );
      return;
    }

    const items = selectedList.map(batch => ({
      batchBarCode: batch.Bar_Code,
      itemName: batch.ItemName,
      batchName: batch.Batch_Name,
      availableQuantity: batch.Quantity,
      expireDate: batch.ExpireDate,
      unitCost: batch.UnitCost,
      atcCode: batch.ATC_Code,
      originalItemBarCode: batch.originalItemBarCode,
      quantity: Number(batch.quantity),
    }));

    onAdd(items);
    setSelected({});
    onHide();
  };

  const rows = showSelectedOnly ? selectedList : data.items;
  const isLoadingRows = !showSelectedOnly && isFetching;
  const emptyMessage = showSelectedOnly
    ? "No items selected yet"
    : "No batches found";

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      animation={false}
      enforceFocus={false}
      size="lg"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">Select Item From Batch</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-3 align-items-center mb-3">
          <MagnifyIcon />
          <Form.Control
            placeholder="Search batch or item..."
            value={queryParams.q}
            onChange={e =>
              setQueryParams(p => ({ ...p, page: 1, q: e.target.value }))
            }
            autoFocus
          />
        </div>

        <Form.Check
          type="switch"
          id="show-selected-only"
          label={`Show selected only (${selectedList.length})`}
          className="mb-3"
          checked={showSelectedOnly}
          onChange={e => setShowSelectedOnly(e.target.checked)}
        />

        <Table borderless hover striped className="product-table">
          <thead className="sticky text-nowrap">
            <tr>
              <th />
              <th scope="col">Batch</th>
              <th scope="col">Item Name</th>
              <th scope="col">ATC Code</th>
              <th scope="col">Unit Cost</th>
              <th scope="col">Available Qty</th>
              <th scope="col">Expiry Date</th>
              <th scope="col">Qty to Damage</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingRows ? (
              <tr>
                <td colSpan={8} className="text-center py-3">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-3">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((el, index) => {
                const alreadyAdded = isAlreadyAdded(el.Bar_Code);
                const isSelected = !!selected[el.Bar_Code];
                return (
                  <tr
                    key={el.Bar_Code || index}
                    className={
                      alreadyAdded
                        ? "text-light"
                        : isSelected
                        ? "table-active"
                        : ""
                    }
                  >
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        disabled={alreadyAdded}
                        onChange={() => toggleChecked(el)}
                      />
                    </td>
                    <td>
                      {el.Bar_Code ? el.Bar_Code.split("-").pop() : "..."}{" "}
                      {el.Batch_Name ? <i>({el.Batch_Name})</i> : null}
                    </td>
                    <td>
                      {el.ItemName}{" "}
                      {alreadyAdded && (
                        <span className="text-light">(Already added)</span>
                      )}
                    </td>
                    <td>{el.ATC_Code || "..."}</td>
                    <td>
                      {currency(el.UnitCost, {
                        precision: 2,
                        symbol: "",
                      }).format()}
                    </td>
                    <td>
                      {currency(el.Quantity, {
                        precision: 2,
                        format: formatForQty,
                        symbol: "",
                      }).format()}
                    </td>
                    <td className="text-nowrap">
                      {el.ExpireDate
                        ? formatDate(el.ExpireDate, "dd MMM, yyyy")
                        : "..."}
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min={0}
                        max={el.Quantity}
                        placeholder="0"
                        style={{ width: "100px" }}
                        disabled={alreadyAdded}
                        value={selected[el.Bar_Code]?.quantity || ""}
                        onChange={e => handleQuantityChange(el, e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 gap-3">
          <div>
            {!showSelectedOnly && data.count > queryParams.limit && (
              <ReactPaginate
                {...paginationOptions}
                pageCount={Math.ceil(data.count / queryParams.limit)}
                forcePage={queryParams.page - 1}
                onPageChange={({ selected }) =>
                  setQueryParams(p => ({ ...p, page: selected + 1 }))
                }
              />
            )}
          </div>
          <Button variant="primary" onClick={handleDone}>
            Done ({selectedList.length} selected)
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
